import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import VipIcon from '../Icons/VipIcons';
import { PrimaryButton, SecondaryButton, GhostButton, IconButton, UnstyledButton } from './Button';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, format, isSameDay, isSameMonth,
  addMonths, subMonths, isBefore, startOfDay,
} from 'date-fns';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** Returns "YYYY-MM-DDTHH:mm" in local time — same format as datetime-local inputs */
function toLocalString(date) {
  const d = new Date(date);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}

function parseLocalString(str) {
  if (!str) return null;
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

// ─── Styled Components ────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000100; /* above Scheduled.js ModalOverlay (10000) and common Modal (10000001) */
  padding: 16px;
`;

const Modal = styled.div`
  background: ${({ theme }) => theme.colors.bg || theme.colors.background || '#ffffff'};
  border-radius: 18px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.4);
  width: 100%;
  max-width: 300px;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;
  user-select: none;
  opacity: 1;
`;

const CalNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px 0;
`;

const NavBtn = styled(IconButton).attrs({ $size: 'small' })``;

const MonthLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const WeekRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  padding: 0 8px;
`;

const WDLabel = styled.span`
  text-align: center;
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 1px 0;
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  padding: 0 8px 4px;
  gap: 1px;
`;

const DayBtn = styled(UnstyledButton)`
  height: 30px;
  width: 30px;
  justify-self: center;
  border: none;
  border-radius: 50%;
  background: ${({ $selected, $today, theme }) =>
    $selected
      ? theme.colors.primary
      : $today
      ? `${theme.colors.primary}25`
      : 'none'};
  color: ${({ $selected, $otherMonth, $disabled, theme }) =>
    $selected
      ? 'white'
      : $disabled
      ? theme.colors.textSecondary
      : $otherMonth
      ? theme.colors.textSecondary
      : theme.colors.text};
  font-size: 12px;
  font-weight: ${({ $selected, $today }) => ($selected || $today ? '700' : '400')};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled, $otherMonth }) => ($disabled ? 0.3 : $otherMonth ? 0.45 : 1)};
  transition: background 0.12s;
  padding: 0;

  &:hover:not(:disabled) {
    background: ${({ $selected, theme }) =>
      $selected ? theme.colors.primary : theme.colors.bg3};
  }
`;

const Sep = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.divider};
  margin: 0 12px;
`;

const TimeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 4px 16px;
`;

const TimeUnit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: ns-resize;
  touch-action: none;
  user-select: none;
`;

const AdjBtn = styled(UnstyledButton)`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  width: 38px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: background 0.12s;

  &:hover {
    background: ${({ theme }) => theme.colors.bg3};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const TimeVal = styled.div`
  font-size: 26px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  min-width: 50px;
  text-align: center;
  background: ${({ theme }) => theme.colors.bg3};
  border-radius: 8px;
  padding: 5px 0;
  line-height: 1.25;
`;

const TimeColon = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  align-self: center;
`;

// ─── Drum-roll picker ──────────────────────────────────────────────────────────

const DRUM_ITEM_H = 34;
const DRUM_VISIBLE = 3;
const DRUM_HALF = Math.floor(DRUM_VISIBLE / 2);
const HOURS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

const DrumOuter = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;

  & > [data-chevron] {
    opacity: 0;
    transition: opacity 0.15s;
  }
  &:hover > [data-chevron] {
    opacity: 1;
  }
`;

const DrumChevron = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  height: 14px;
  cursor: pointer;
  user-select: none;
`;

const DrumWrap = styled.div`
  position: relative;
  width: 56px;
  height: ${DRUM_ITEM_H * DRUM_VISIBLE}px;
  overflow: hidden;
  user-select: none;
  touch-action: none;
  flex-shrink: 0;
`;

const DrumList = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  will-change: transform;
`;

const DrumItem = styled.div`
  height: ${DRUM_ITEM_H}px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $center }) => ($center ? '18px' : '12px')};
  font-weight: ${({ $center }) => ($center ? '700' : '400')};
  color: ${({ $center, theme }) =>
    $center ? theme.colors.text : theme.colors.textSecondary};
  transition: font-size 0.08s, color 0.08s;
  line-height: 1;
`;

const DrumFadeTop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: ${DRUM_ITEM_H * DRUM_HALF}px;
  background: linear-gradient(
    to bottom,
    ${({ theme }) => theme.colors.bg || theme.colors.background || '#fff'} 0%,
    transparent 100%
  );
  pointer-events: none;
  z-index: 2;
`;

const DrumFadeBottom = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${DRUM_ITEM_H * DRUM_HALF}px;
  background: linear-gradient(
    to top,
    ${({ theme }) => theme.colors.bg || theme.colors.background || '#fff'} 0%,
    transparent 100%
  );
  pointer-events: none;
  z-index: 2;
`;

const DrumSelection = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: ${DRUM_ITEM_H * DRUM_HALF}px;
  height: ${DRUM_ITEM_H}px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  pointer-events: none;
  z-index: 1;
`;

const AmPmWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 4px;
`;

const AmPmBtn = styled(UnstyledButton)`
  padding: 7px 11px;
  border-radius: 7px;
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary : 'none'};
  color: ${({ $active, theme }) => ($active ? 'white' : theme.colors.text)};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.12s;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 12px 8px;
  gap: 6px;
`;

const ClearBtn = styled(GhostButton).attrs({ $size: 'small' })`
  margin-right: auto;
`;



// ─── Component ────────────────────────────────────────────────────────────────

function DrumRoll({ value, values, onChange, onStep, dragActiveRef }) {
  const n = values.length;
  const [pixelOffset, setPixelOffset] = useState(0);
  const [animOffset, setAnimOffset] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const dragRef = useRef(null);

  const currentIdx = values.indexOf(value);
  // Which value is visually centered during drag
  const dragSteps = Math.round(-pixelOffset / DRUM_ITEM_H);
  const centerIdx  = ((currentIdx + dragSteps) % n + n) % n;

  // Render a window of items around currentIdx with a buffer on each side
  const BUF = 8;
  const lo  = currentIdx - DRUM_HALF - BUF;
  const hi  = currentIdx + DRUM_HALF + BUF;
  const wrap = (i) => ((i % n) + n) % n;

  // translateY so item at currentIdx is centred in the container, offset by pixelOffset
  // item[lo] is at list-top 0; item[currentIdx] is at (currentIdx - lo)*DRUM_ITEM_H
  // we want item[currentIdx] at DRUM_HALF*DRUM_ITEM_H + pixelOffset
  // → translateY = (DRUM_HALF - (currentIdx - lo)) * DRUM_ITEM_H + pixelOffset
  //              = -BUF * DRUM_ITEM_H + pixelOffset
  const translateY = -BUF * DRUM_ITEM_H + pixelOffset;

  const startDrag = (clientY) => {
    dragRef.current = { startY: clientY, startIdx: currentIdx };
    setPixelOffset(0);
  };

  const moveDrag = (clientY) => {
    if (!dragRef.current) return;
    setPixelOffset(clientY - dragRef.current.startY);
  };

  const endDrag = (clientY) => {
    if (!dragRef.current) return;
    const dy    = clientY - dragRef.current.startY;
    const steps = Math.round(-dy / DRUM_ITEM_H);
    if (onStep) {
      onStep(steps);
    } else {
      const newIdx = ((dragRef.current.startIdx + steps) % n + n) % n;
      onChange(values[newIdx]);
    }
    setPixelOffset(0);
    dragRef.current = null;
  };

  const handleChevronStep = (delta) => {
    if (!onStep) return;
    // Instantly pre-position the drum so the new value appears displaced (no jump)
    setAnimOffset(-delta * DRUM_ITEM_H);
    setIsAnimating(false);
    onStep(delta);
    // Two rAFs: first lets React flush the snap render, second triggers the transition
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsAnimating(true);
        setAnimOffset(0);
      });
    });
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    if (dragActiveRef) dragActiveRef.current = true;
    startDrag(e.clientY);
    const onMove = (ev) => moveDrag(ev.clientY);
    const onUp   = (ev) => {
      endDrag(ev.clientY);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      // Delay so the overlay's click event (which fires after mouseup) sees the flag
      setTimeout(() => { if (dragActiveRef) dragActiveRef.current = false; }, 0);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <DrumOuter>
      <DrumChevron data-chevron="up" onClick={() => handleChevronStep(1)}>
        <VipIcon name="ChevronUp" size={14} />
      </DrumChevron>
      <DrumWrap
        onMouseDown={handleMouseDown}
        onTouchStart={(e) => startDrag(e.touches[0].clientY)}
        onTouchMove={(e) => { e.preventDefault(); moveDrag(e.touches[0].clientY); }}
        onTouchEnd={(e) => endDrag(e.changedTouches[0]?.clientY ?? dragRef.current?.startY ?? 0)}
      >
        <DrumFadeTop />
        <DrumSelection />
        <DrumFadeBottom />
        <DrumList
          style={{
            transform: `translateY(${translateY + animOffset}px)`,
            transition: isAnimating ? 'transform 120ms ease-out' : 'none',
          }}
          onTransitionEnd={() => setIsAnimating(false)}
        >
          {Array.from({ length: hi - lo + 1 }, (_, i) => {
            const relIdx  = lo + i;
            const isCenter = wrap(relIdx) === centerIdx;
            return (
              <DrumItem key={relIdx} $center={isCenter}>
                {String(values[wrap(relIdx)]).padStart(2, '0')}
              </DrumItem>
            );
          })}
        </DrumList>
      </DrumWrap>
      <DrumChevron data-chevron="down" onClick={() => handleChevronStep(-1)}>
        <VipIcon name="ChevronDown" size={14} />
      </DrumChevron>
    </DrumOuter>
  );
}

/**
 * DateTimePickerModal
 *
 * Props:
 *   isOpen   {boolean}
 *   onClose  {() => void}
 *   onConfirm {(localString: string) => void}  — "YYYY-MM-DDTHH:mm" local time
 *   onClear  {() => void}  — optional; shows a Clear button when value is set
 *   value    {string}  — current "YYYY-MM-DDTHH:mm" value or ""
 *   minDate  {Date}    — minimum selectable datetime (default: now + 1 min)
 *   title    {string}  — modal title (default: "Schedule")
 */
export default function DateTimePickerModal({
  isOpen,
  onClose,
  onConfirm,
  onClear,
  value = '',
  minDate,
  title = 'Schedule',
}) {
  const effectiveMin =
    minDate instanceof Date ? minDate : new Date(Date.now() + 60000);

  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [hour, setHour] = useState(12);
  const [minute, setMinute] = useState(0);
  const [ampm, setAmpm] = useState('AM');
  const drumDraggingRef = useRef(false);

  // Reset / initialize state each time the modal opens
  useEffect(() => {
    if (!isOpen) return;
    const parsed = parseLocalString(value);
    const base = parsed || new Date(Date.now() + 60000); // default to local now + 1 min
    setViewDate(new Date(base));
    setSelectedDay(startOfDay(base));
    const h = base.getHours();
    setHour(h % 12 || 12);
    // Round up to next 5-min increment
    setMinute(base.getMinutes());
    setAmpm(h >= 12 ? 'PM' : 'AM');
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Calendar ────────────────────────────────────────────────────────────────

  const calDays = eachDayOfInterval({
    start: startOfWeek(startOfMonth(viewDate)),
    end: endOfWeek(endOfMonth(viewDate)),
  });

  const isDayDisabled = (day) =>
    isBefore(startOfDay(day), startOfDay(effectiveMin));

  // ── Time helpers ────────────────────────────────────────────────────────────

  const get24Hour = () => {
    if (ampm === 'AM') return hour === 12 ? 0 : hour;
    return hour === 12 ? 12 : hour + 12;
  };

  const getDateTime = () => {
    if (!selectedDay) return null;
    const d = new Date(selectedDay);
    d.setHours(get24Hour(), minute, 0, 0);
    return d;
  };

  // adjHourBy: handles any step count; flips AM/PM each time the drum crosses
  // the 12→1 (forward) or 1→12 (backward) boundary.
  const adjHourBy = (delta) => {
    const index = hour - 1; // map 1-12 → 0-11
    const newIndex = ((index + delta) % 12 + 12) % 12;
    const flips = Math.floor((index + delta) / 12);
    setHour(newIndex + 1);
    if (flips % 2 !== 0) setAmpm((ap) => (ap === 'AM' ? 'PM' : 'AM'));
  };

  // adjMinBy: handles any step count; carries into hours on overflow.
  const adjMinBy = (delta) => {
    const total = minute + delta;
    setMinute(((total % 60) + 60) % 60);
    const carry = Math.floor(total / 60);
    if (carry !== 0) adjHourBy(carry);
  };

  // ── Actions ─────────────────────────────────────────────────────────────────

  const isConfirmDisabled = () => {
    const dt = getDateTime();
    return !dt || dt < effectiveMin;
  };

  const handleConfirm = () => {
    const dt = getDateTime();
    if (!dt || dt < effectiveMin) return;
    onConfirm(toLocalString(dt));
    onClose();
  };

  const handleClear = () => {
    if (onClear) onClear();
    onClose();
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (!isOpen) return null;

  const previewDt = getDateTime();

  return ReactDOM.createPortal(
    <Overlay onClick={() => { if (!drumDraggingRef.current) onClose(); }}>
      <Modal onClick={(e) => e.stopPropagation()}>

        {/* ── Calendar nav ── */}
        <CalNav>
          <NavBtn
            type="button"
            onClick={() => setViewDate((d) => subMonths(d, 1))}
            aria-label="Previous month"
          >
            <VipIcon name="ChevronLeft" size={18} />
          </NavBtn>
          <MonthLabel>{format(viewDate, 'MMMM yyyy')}</MonthLabel>
          <NavBtn
            type="button"
            onClick={() => setViewDate((d) => addMonths(d, 1))}
            aria-label="Next month"
          >
            <VipIcon name="ChevronRight" size={18} />
          </NavBtn>
        </CalNav>

        {/* ── Weekday labels ── */}
        <WeekRow>
          {WEEKDAYS.map((d) => (
            <WDLabel key={d}>{d}</WDLabel>
          ))}
        </WeekRow>

        {/* ── Day grid ── */}
        <DaysGrid>
          {calDays.map((day) => {
            const disabled = isDayDisabled(day);
            const selected = selectedDay && isSameDay(day, selectedDay);
            const today = isSameDay(day, new Date());
            const otherMonth = !isSameMonth(day, viewDate);
            return (
              <DayBtn
                key={day.toISOString()}
                type="button"
                $selected={selected}
                $today={today}
                $disabled={disabled}
                $otherMonth={otherMonth}
                disabled={disabled}
                onClick={() => setSelectedDay(startOfDay(day))}
              >
                {format(day, 'd')}
              </DayBtn>
            );
          })}
        </DaysGrid>

        <Sep />

        {/* ── Time picker ── */}
        <TimeRow>
          {/* Hour */}
          <DrumRoll values={HOURS} value={hour} onChange={setHour} onStep={adjHourBy} dragActiveRef={drumDraggingRef} />

          <TimeColon>:</TimeColon>

          {/* Minute */}
          <DrumRoll values={MINUTES} value={minute} onChange={setMinute} onStep={adjMinBy} dragActiveRef={drumDraggingRef} />

          {/* AM / PM */}
          <AmPmWrap>
            <AmPmBtn
              type="button"
              $active={ampm === 'AM'}
              onClick={() => setAmpm('AM')}
            >
              AM
            </AmPmBtn>
            <AmPmBtn
              type="button"
              $active={ampm === 'PM'}
              onClick={() => setAmpm('PM')}
            >
              PM
            </AmPmBtn>
          </AmPmWrap>
        </TimeRow>

        {/* ── Footer ── */}
        <Footer>
          {onClear && value && (
            <ClearBtn type="button" onClick={handleClear}>
              Clear
            </ClearBtn>
          )}
          <SecondaryButton type="button" $size="small" onClick={onClose} style={{ flex: 1 }}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="button"
            $size="small"
            onClick={handleConfirm}
            disabled={isConfirmDisabled()}
            style={{ flex: 1 }}
          >
            Confirm
          </PrimaryButton>
        </Footer>

      </Modal>
    </Overlay>,
    document.body
  );
}
